// LearnCore Frontend - 标签编辑组件

import { useState, useEffect } from 'react';
import { apiClient } from '../api/client';
import { PencilIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { InlineLoading } from './Loading';
import type { Tag } from '../types';

// 标签缓存机制
const tagCache = {
  grade: { data: null as Tag[] | null, timestamp: 0, promise: null as Promise<Tag[]> | null },
  subject: { data: null as Tag[] | null, timestamp: 0, promise: null as Promise<Tag[]> | null },
  custom: { data: null as Tag[] | null, timestamp: 0, promise: null as Promise<Tag[]> | null }
};

const CACHE_DURATION = 5 * 60 * 1000; // 5分钟缓存

const loadTagsWithCache = async (type: 'grade' | 'subject' | 'custom'): Promise<Tag[]> => {
  const now = Date.now();
  const cache = tagCache[type];
  
  // 如果缓存有效，直接返回
  if (cache.data && (now - cache.timestamp) < CACHE_DURATION) {
    return cache.data;
  }
  
  // 如果正在请求，返回同一个 Promise
  if (cache.promise) {
    return cache.promise;
  }
  
  // 发起新请求
  cache.promise = apiClient.getTags(type).then(response => {
    cache.data = response.tags || [];
    cache.timestamp = now;
    cache.promise = null;
    return cache.data;
  }).catch(error => {
    cache.promise = null;
    throw error;
  });
  
  return cache.promise;
};

// 添加标签到缓存（用于新创建的标签）
const addTagToCache = (tag: Tag) => {
  const cache = tagCache[tag.type];
  if (cache.data) {
    // 检查是否已存在，避免重复添加
    const exists = cache.data.some(t => t.id === tag.id);
    if (!exists) {
      cache.data.push(tag);
    }
  }
};

// 清除缓存（用于强制刷新）
export const clearTagCache = (type?: 'grade' | 'subject' | 'custom') => {
  if (type) {
    tagCache[type].data = null;
    tagCache[type].timestamp = 0;
    tagCache[type].promise = null;
  } else {
    // 清除所有缓存
    Object.keys(tagCache).forEach(key => {
      const cache = tagCache[key as keyof typeof tagCache];
      cache.data = null;
      cache.timestamp = 0;
      cache.promise = null;
    });
  }
};

interface TagEditorProps {
  setId?: string; // 编辑模式下的学习集ID
  passageId?: string; // 文言文材料ID
  initialTags?: Tag[]; // 初始标签
  onTagsChange?: (tagIds: string[], tags?: Tag[]) => void; // 标签变化回调
  disabled?: boolean;
  entityType?: 'set' | 'passage' | 'classical-passage'; // 实体类型
}

export default function TagEditor({ setId, passageId, initialTags = [], onTagsChange, disabled = false, entityType = 'set' }: TagEditorProps) {
  // 确保 initialTags 始终是数组
  const safeInitialTags = Array.isArray(initialTags) ? initialTags : [];
  const [isEditing, setIsEditing] = useState(false);
  const [selectedTagIds, setSelectedTagIds] = useState<Set<string>>(new Set());
  
  // 预置标签
  const [gradeTags, setGradeTags] = useState<Tag[]>([]);
  const [subjectTags, setSubjectTags] = useState<Tag[]>([]);
  const [customTags, setCustomTags] = useState<Tag[]>([]);
  
  // 自由输入
  const [customInput, setCustomInput] = useState('');
  
  // 状态
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadTags();
  }, []);

  useEffect(() => {
    if (safeInitialTags.length > 0) {
      setSelectedTagIds(new Set(safeInitialTags.map(t => t.id)));
    }
  }, [safeInitialTags]);

  const loadTags = async () => {
    try {
      setLoading(true);
      const [grades, subjects, customs] = await Promise.all([
        loadTagsWithCache('grade'),
        loadTagsWithCache('subject'),
        loadTagsWithCache('custom')
      ]);
      setGradeTags(grades);
      setSubjectTags(subjects);
      setCustomTags(customs);
    } catch (e: any) {
      console.warn('加载标签失败', e);
    } finally {
      setLoading(false);
    }
  };

  const toggleTag = (tagId: string) => {
    setSelectedTagIds(prev => {
      const next = new Set(prev);
      if (next.has(tagId)) next.delete(tagId); else next.add(tagId);
      return next;
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      // 处理自由输入的标签
      if (customInput.trim()) {
        const inputTags = customInput.split(/[,;，；]/).map(s => s.trim()).filter(Boolean);
        
        for (const name of inputTags) {
          // 检查是否已存在
          const existing = [...gradeTags, ...subjectTags, ...customTags].find(t => t.name === name);
          if (existing) {
            setSelectedTagIds(prev => new Set(prev).add(existing.id));
          } else {
            // 创建新标签
            try {
              const resp = await apiClient.createCustomTag(name);
              const newTag = resp.tag;
              setCustomTags(prev => [...prev, newTag]);
              setSelectedTagIds(prev => new Set(prev).add(newTag.id));
              // 更新缓存
              addTagToCache(newTag);
            } catch (err: any) {
              console.error('创建标签失败:', name, err);
            }
          }
        }
        setCustomInput('');
      }

      // 根据实体类型保存到服务器
      if (entityType === 'set' && setId) {
        await apiClient.applyTagsToSet(setId, Array.from(selectedTagIds));
      } else if (entityType === 'classical-passage' && passageId) {
        await apiClient.applyTagsToClassicalPassage(passageId, Array.from(selectedTagIds));
      }

      // 通知父组件
      if (onTagsChange) {
        const allTags = [...gradeTags, ...subjectTags, ...customTags];
        const selectedTags = allTags.filter(t => selectedTagIds.has(t.id));
        onTagsChange(Array.from(selectedTagIds), selectedTags);
      }

      setIsEditing(false);
    } catch (err: any) {
      setError(err.response?.data?.error || '保存标签失败');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    // 恢复到初始状态
    setSelectedTagIds(new Set(safeInitialTags.map(t => t.id)));
    setCustomInput('');
    setError(null);
    setIsEditing(false);
  };

  // 获取所有标签用于展示
  const allTags = [...gradeTags, ...subjectTags, ...customTags];
  const selectedTags = allTags.filter(t => selectedTagIds.has(t.id));

  if (loading) {
    return (
      <div className="flex items-center text-sm text-gray-500">
        <InlineLoading size="sm" />
        <span className="ml-2">加载标签...</span>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* 非编辑状态 */}
      {!isEditing && (
        <div className="flex items-center justify-between">
          <div className="flex-1 flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-gray-700">标签：</span>
            {selectedTags.length > 0 ? (
              selectedTags.map(tag => (
                <span
                  key={tag.id}
                  className="px-2 py-1 rounded-full text-xs bg-primary-50 text-primary-700 border border-primary-200"
                >
                  {tag.name}
                </span>
              ))
            ) : (
              <span className="text-sm text-gray-400">未设置标签</span>
            )}
          </div>
          <button
            type="button"
            onClick={() => setIsEditing(true)}
            disabled={disabled}
            className="ml-3 text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1 disabled:opacity-50"
          >
            <PencilIcon className="h-4 w-4" />
            编辑
          </button>
        </div>
      )}

      {/* 编辑状态 */}
      {isEditing && (
        <div className="border border-gray-200 rounded-lg p-4 bg-gray-50 space-y-4">
          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
              {error}
            </div>
          )}

          {/* 年级选择 */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-2">年级（预置）</label>
            <div className="flex flex-wrap gap-2">
              {gradeTags.map(tag => (
                <button
                  key={tag.id}
                  type="button"
                  onClick={() => toggleTag(tag.id)}
                  className={`px-3 py-1 rounded-full text-sm border transition-colors ${
                    selectedTagIds.has(tag.id)
                      ? 'bg-primary-100 text-primary-800 border-primary-300'
                      : 'bg-white text-gray-700 border-gray-300 hover:border-primary-300'
                  }`}
                >
                  {tag.name}
                </button>
              ))}
              {gradeTags.length === 0 && (
                <span className="text-xs text-gray-400">暂无预置年级标签</span>
              )}
            </div>
          </div>

          {/* 学科选择 */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-2">学科（预置）</label>
            <div className="flex flex-wrap gap-2">
              {subjectTags.map(tag => (
                <button
                  key={tag.id}
                  type="button"
                  onClick={() => toggleTag(tag.id)}
                  className={`px-3 py-1 rounded-full text-sm border transition-colors ${
                    selectedTagIds.has(tag.id)
                      ? 'bg-primary-100 text-primary-800 border-primary-300'
                      : 'bg-white text-gray-700 border-gray-300 hover:border-primary-300'
                  }`}
                >
                  {tag.name}
                </button>
              ))}
              {subjectTags.length === 0 && (
                <span className="text-xs text-gray-400">暂无预置学科标签</span>
              )}
            </div>
          </div>

          {/* 自定义标签 */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-2">自定义标签</label>
            {customTags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2">
                {customTags.map(tag => (
                  <button
                    key={tag.id}
                    type="button"
                    onClick={() => toggleTag(tag.id)}
                    className={`px-3 py-1 rounded-full text-sm border transition-colors ${
                      selectedTagIds.has(tag.id)
                        ? 'bg-primary-100 text-primary-800 border-primary-300'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-primary-300'
                    }`}
                  >
                    {tag.name}
                  </button>
                ))}
              </div>
            )}
            <div className="flex items-center gap-2">
              <input
                type="text"
                placeholder="输入标签，用逗号或分号分隔（如：重点,难点）"
                className="input text-sm flex-1"
                value={customInput}
                onChange={(e) => setCustomInput(e.target.value)}
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">
              💡 可输入多个标签，用 <code className="px-1 bg-gray-100 rounded">，</code> 或 <code className="px-1 bg-gray-100 rounded">；</code> 分隔
            </p>
          </div>

          {/* 操作按钮 */}
          <div className="flex justify-end gap-2 pt-2 border-t border-gray-200">
            <button
              type="button"
              onClick={handleCancel}
              disabled={saving}
              className="btn-outline text-sm"
            >
              <XMarkIcon className="h-4 w-4 mr-1" />
              取消
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="btn-primary text-sm"
            >
              {saving ? (
                <>
                  <InlineLoading size="sm" />
                  <span className="ml-1">保存中...</span>
                </>
              ) : (
                <>
                  <CheckIcon className="h-4 w-4 mr-1" />
                  保存
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

